<template>
  <div class="did-card">
    <div class="did-card-head">
      <DecastLogo />
      <span class="did-card-title">DecastID</span>
      <span class="did-verified">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 1L10 3H13V6L15 8L13 10V13H10L8 15L6 13H3V10L1 8L3 6V3H6L8 1Z"
            fill="#D7DF23"
          />
          <path
            d="M6 8L7.5 9.5L10.5 6.5"
            stroke="#0F1017"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </div>
    <p class="did-card-desc">
      Own your identity. No passwords, no platforms tracking you.
    </p>
    <button
      class="did-btn"
      @click="handleDidsLogin"
      :disabled="isLoading"
      type="button"
    >
      <svg
        class="did-shield"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M8 1.5L2.5 4V7.5C2.5 11 5 13.5 8 14.5C11 13.5 13.5 11 13.5 7.5V4L8 1.5Z"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <path
          d="M6 8L7.5 9.5L10 6.5"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      Sign in with DecastID
    </button>
    <div class="did-trust">
      <span>Decentralized</span>
      <span class="did-dot"></span>
      <span>Cryptographic</span>
      <span class="did-dot"></span>
      <span>Private</span>
    </div>
  </div>
</template>

<script>
import { afterAuthPath } from '@/http/requests/billing';
import DidIcon from '@/decast/icons/DidIcon.vue';
import DecastLogo from '@/decast/icons/DecastLogo.vue';
import constants from '../../../../../constant';
const EXTENSION_ID = constants.didManagerExtensionKey;

export default {
  name: 'DidsLogin',
  components: {
    DidIcon,
    DecastLogo,
  },
  data() {
    return {
      nonce: '',
      isLoading: false,
      errorMessage: '',
      selectedDid: '',
      selectedUsername: '',
      boundNonceHandler: null,
    };
  },
  async mounted() {
    document.getElementById('loading-bg-transparent').style.display = 'none';
    window.addEventListener('message', this.handleDidSelected);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleDidSelected);
  },
  methods: {
    async handleDidsLogin() {
      this.isLoading = true;
      this.errorMessage = '';

      if (window.chrome && chrome.runtime) {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: 'open-did-popup' },
          (response) => {
            if (chrome.runtime.lastError) {
              this.isLoading = false;
              this.errorMessage = 'Extension not available';
            }
          }
        );

        window.addEventListener('message', this.handleDidSelected);
      } else {
        this.isLoading = false;
        this.errorMessage = 'Extension API not available';
        this.$vs.notify({
          title: 'Request Unsuccessful',
          text: 'Extension not installed or not available',
          iconPack: 'feather',
          icon: 'icon-check-circle',
          color: 'danger',
        });
        window.location.href =
          'https://chrome.google.com/webstore/detail/decast-did-manager/' +
          EXTENSION_ID;
      }
    },

    async handleDidSelected(event) {
      if (event.data?.action !== 'did-selected') return;

      window.removeEventListener('message', this.handleDidSelected);

      try {
        const { did, username } = event.data;

        if (typeof did === 'string') {
          this.selectedDid = did;
          this.selectedUsername = username || '';
        } else {
          this.selectedDid = did?.did || '';
          this.selectedUsername = did?.username || username || '';
        }

        const payload = { did: this.selectedDid };
        this.$store
          .dispatch('auth/v2getDidNonce', payload)
          .then((response) => {
            const { nonce, token } = response.data;
            chrome.runtime.sendMessage(
              EXTENSION_ID,
              { action: 'sign-nonce', nonce, did },
              (response) => {
                if (chrome.runtime.lastError) {
                  this.errorMessage = 'Error communicating with extension';
                } else {
                  this.$vs.notify({
                    title: 'Success',
                    text: 'Nonce sent to extension for signing',
                    iconPack: 'feather',
                    icon: 'icon-check-circle',
                    color: 'success',
                  });
                }
              }
            );

            this.boundNonceHandler = (e) => this.handleNonceSigned(e, token);
            window.addEventListener('message', this.boundNonceHandler);
          })
          .catch((error) => {
            const errorResponse = error.response?.data;
            let message = 'An error occurred.';
            if (errorResponse?.errors?.email) {
              message = 'Account does not exist!';
            } else if (typeof errorResponse?.errors === 'string') {
              message = errorResponse.errors;
            }
          })
          .finally(() => {});
      } catch (err) {
        this.isLoading = false;
        this.errorMessage = err.message;
      }
    },

    async handleNonceSigned(event, token) {
      if (event.data?.action !== 'nonce-signed') return;

      if (this.boundNonceHandler) {
        window.removeEventListener('message', this.boundNonceHandler);
        this.boundNonceHandler = null;
      }

      if (event.data.error) {
        this.$vs.notify({
          title: 'Nonce Signing Canceled',
          text: event.data.error,
          iconPack: 'feather',
          icon: 'icon-check-circle',
          color: 'warning',
        });
        this.isLoading = false;
        this.errorMessage = event.data.error;
        return;
      }

      const { signature } = event.data;

      try {
        const didDocument = {
          '@context': 'https://www.w3.org/ns/did/v1',
          id: this.selectedDid,
          verificationMethod: [
            {
              id: `${this.selectedDid}#keys-1`,
              type: 'Ed25519VerificationKey2018',
              controller: this.selectedDid,
              publicKeyBase58: this.selectedDid.split(':').pop(),
            },
          ],
          authentication: [`${this.selectedDid}#keys-1`],
        };

        const payload = {
          token,
          signature,
          didDocument,
          username: this.selectedUsername || undefined,
        };
        this.$store
          .dispatch('auth/v2loginWithDid', payload)
          .then((response) => {
            const isTwoFactorAuthEnabled =
              response.data?.twoFactorAuthentication;
            if (isTwoFactorAuthEnabled) {
              const twoFactorToken = response.data?.token;
              this.$router.push({
                name: 'TwoFactor',
                query: { tempToken: twoFactorToken },
              });
            } else {
              this.$store.commit('auth/SET_LOGGEDIN', true);
              this.$acl.change('user');
              this.$vs.notify({
                title: 'Login Successful',
                text: 'Redirecting to dashboard!',
                iconPack: 'feather',
                icon: 'icon-check-circle',
                color: 'success',
              });
              this.$router.push(afterAuthPath('/dashboard/rooms'));
            }
          })
          .catch((error) => {
            console.log(error, 'login error');
          })
          .finally(() => {
            this.isLoading = false;
            document.getElementById('loading-bg-transparent').style.display =
              'none';
          });
      } catch (err) {
        this.errorMessage = err.message;
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.did-card {
  width: 100%;
  background: rgba(215, 223, 35, 0.03);
  border: 1px solid rgba(215, 223, 35, 0.1);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.did-card-head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
}

.did-card-head svg {
  width: 18px;
  height: 25px;
  flex-shrink: 0;
}

.did-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Rethink Sans', sans-serif;
  letter-spacing: -0.01em;
}

.did-verified {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.did-card-desc {
  color: #9b9db0;
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 16px 0;
  font-family: 'Rethink Sans', sans-serif;
}

.did-btn {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #d7df23;
  color: #0f1017;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.1s;
  font-family: 'Rethink Sans', sans-serif;
}

.did-btn:hover {
  background: #c5cc1f;
}

.did-btn:active {
  transform: scale(0.985);
}

.did-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.did-shield {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.did-trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
}

.did-trust span {
  color: #6b6d7e;
  font-size: 11px;
  font-family: 'Rethink Sans', sans-serif;
  letter-spacing: 0.02em;
}

.did-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #3e4050;
  flex-shrink: 0;
}
</style>

<style scoped>
/* The DecastID flow needs the DID-manager browser extension, which doesn't
   exist on mobile browsers - hide the option there entirely. */
@media (max-width: 767px) {
  .did-card {
    display: none !important;
  }
}
</style>
